'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const faqs = [
  {
    question: 'Is this session really completely free?',
    answer: 'Yes! This is a 100% FREE session with no hidden costs or commitments. We believe in giving back to the education community and helping students succeed in their careers.'
  },
  {
    question: 'How long is the session?',
    answer: 'The session is 90 minutes long - packed with valuable insights, practical tips, and interactive discussions. We respect your time and ensure every minute counts.'
  },
  {
    question: 'Can we conduct this session online or does it have to be on campus?',
    answer: 'We offer both options! We can conduct the session on your campus for a more engaging experience, or we can do it online via Zoom/Google Meet if that works better for your schedule.'
  },
  {
    question: 'What is the ideal batch size for this session?',
    answer: 'The session works well for groups ranging from 50 to 500+ students. We adjust our delivery format to ensure maximum engagement regardless of the batch size.'
  },
  {
    question: 'Which year students should attend?',
    answer: 'This session is most beneficial for final year students (3rd and 4th year) who are preparing for campus placements or planning to enter the job market soon. However, pre-final year students can also benefit from early career awareness.'
  },
  {
    question: 'What departments/branches can attend?',
    answer: 'While the session is especially relevant for Computer Science, IT, and related engineering branches, the career preparation strategies and interview tips are valuable for students from all technical backgrounds.'
  },
  {
    question: 'Do you provide any materials or resources after the session?',
    answer: 'Yes! All attending students receive a digital career preparation guide with key takeaways, recommended resources, and a personalized roadmap they can follow for their interview preparation.'
  },
  {
    question: 'Will there be any sales pitch or course promotion?',
    answer: 'The session is purely educational and focused on providing value to students. While we may briefly mention our programs at the end for interested students, there is no hard selling or pressure.'
  },
  {
    question: 'How soon can you conduct the session after we book?',
    answer: 'We typically need 7-10 days advance notice to schedule a session. However, we try our best to accommodate urgent requests based on our availability.'
  },
  {
    question: 'Can we schedule multiple sessions for different departments?',
    answer: 'Absolutely! Many colleges schedule sessions for different departments or batches. We\'re happy to conduct multiple sessions to reach more students.'
  },
  {
    question: 'Who conducts these sessions?',
    answer: 'Sessions are conducted by industry professionals with 10+ years of experience in tech companies, recruitment, and training. They bring real-world insights and practical knowledge that students can immediately apply.'
  },
  {
    question: 'What equipment or setup do you need from our side?',
    answer: 'For on-campus sessions: A projector/screen, microphone (for large batches), and a laptop connection. For online sessions: Just share the meeting link with students. We handle everything else!'
  }
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-card">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Icon name="QuestionMarkCircleIcon" size={20} variant="solid" />
            <span className="text-sm font-headline font-semibold">Common Questions</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-muted-foreground font-body">
            Everything you need to know about booking a session for your college
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-surface rounded-xl border border-border overflow-hidden hover:border-primary transition-colors duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-primary/5 transition-colors duration-300"
              >
                <span className="font-headline font-semibold text-foreground pr-8">
                  {faq.question}
                </span>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  variant="outline"
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5 pt-2">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Box */}
        <div className="mt-12 bg-gradient-to-r from-primary/5 via-accent/5 to-sky-light/5 rounded-xl p-8 border border-primary/20 text-center">
          <Icon name="ChatBubbleLeftRightIcon" size={40} variant="outline" className="text-primary mx-auto mb-4" />
          <h3 className="text-xl font-headline font-bold text-foreground mb-2">
            Have More Questions?
          </h3>
          <p className="text-muted-foreground mb-4">
            Our team is ready to answer any questions and help you plan the perfect session for your students
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+918055579698"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-headline font-semibold hover:bg-primary/90 transition-colors duration-300"
            >
              <Icon name="PhoneIcon" size={20} variant="solid" />
              <span>Call Us Now</span>
            </a>
            <a
              href="mailto:info@vitiquest.com?subject=College%20Session%20Enquiry&body=Hello%20VitiQuest%20Team%2C%0D%0A%0D%0AI%20would%20like%20to%20book%20a%20FREE%20AI%20%26%20Job%20Awareness%20Session%20for%20our%20college.%0D%0A%0D%0ACollege%20Name%3A%20%0D%0ADepartment%3A%20%0D%0AMy%20Designation%3A%20%0D%0AExpected%20Students%3A%20%0D%0APreferred%20Date%3A%20%0D%0A%0D%0AThank%20you!"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-card border-2 border-primary text-primary rounded-lg font-headline font-semibold hover:bg-primary/5 transition-colors duration-300"
            >
              <Icon name="EnvelopeIcon" size={20} variant="solid" />
              <span>Email Us</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
